/* eslint-disable react-native/no-inline-styles */
import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import i18n from 'i18n-js';

import { isAndroid, isPhone, isWeb, navigateTo, SCREEN_PADDING_HORIZONTAL } from '@helpers';

import { SmallLoading } from '@components/loading';
import TouchableElement from '@components/common/touchableElement';
import { font as initialFont } from '@styles/fonts';
import { scale as initialScale, width } from '@components/frame';
import Svg from '@styles/icons/svg';

const DefaultValue = ({
  pressHoverColor,
  item,
  item: { ValueIcon },
  C,
  textColor,
  valueColor,
  isSuperBig,
  takePhoneLayout,
  nameAlreadyTranslated,
  valueAlreadyTranslated,
  onlyLabel,
  scale,
  font,
}) => {
  const noArrow = item.noArrow && !isSuperBig;
  const hideArrow = !item.hideArrow || isSuperBig;
  const valueExist = item.value != null;

  return (
    <View
      style={[
        {
          paddingRight: noArrow ? (isPhone && hideArrow ? 0 : scale(15)) : scale(15),
          justifyContent: 'space-between',
          alignItems: 'center',
          flexDirection: 'row',
          height: scale(41.5) - (isAndroid ? StyleSheet.hairlineWidth : 0),
        },
      ]}
      key={item.name}
    >
      <View style={[!isPhone && { flex: 1 }]}>
        <Text
          style={[
            {
              color: C(pressHoverColor(textColor)),
            },
            font.Body,
          ]}
        >
          {item.name ? (nameAlreadyTranslated ? item.name : i18n.t(item.name)) : ''}
        </Text>
      </View>
      {!onlyLabel && (
        <View
          style={[
            { flexDirection: 'row', alignItems: 'center' },
            isPhone || takePhoneLayout ? { justifyContent: 'flex-end', flex: 1, marginLeft: scale(15) } : { justifyContent: 'space-between', flex: 2 },
          ]}
        >
          {isSuperBig ? (
            <View />
          ) : item.isLoading ? (
            <View style={{ paddingRight: scale(item.hideArrow ? 0 : 6) }}>
              <SmallLoading />
            </View>
          ) : (
            <View style={[{ paddingRight: scale(item.hideArrow ? 0 : 12) }, !takePhoneLayout && !isPhone && { flex: 1 }]}>
              {item.valueFunction ? (
                item.value()
              ) : (
                <Text
                  numberOfLines={1}
                  ellipsizeMode="tail"
                  style={[
                    {
                      color: C(pressHoverColor(valueColor)),
                      fontVariant: ['tabular-nums'],
                    },
                    font.Body,
                  ]}
                >
                  {ValueIcon && <ValueIcon />}
                  {valueExist ? (valueAlreadyTranslated ? String(item.value) : i18n.t(String(item.value))) : ''}
                </Text>
              )}
            </View>
          )}
          {item.alert && (
            <View
              style={{
                marginRight: scale(20),
                width: scale(24),
                height: scale(24),
                borderRadius: scale(24) / 2,
                backgroundColor: C('Status/Light/Missing'),
                justifyContent: 'center',
                alignContent: 'center',
                alignItems: 'center',
              }}
            >
              <Text style={[font.Body, { color: C('Label/Light/AlwaysDark Primary') }]}>{item.alert}</Text>
            </View>
          )}
          {hideArrow && (
            <View style={{ opacity: noArrow ? 0 : 1 }}>
              <Svg icon="chevronRightLight" color="Label/Light/Quinary" width={scale(7)} height={scale(12)} />
            </View>
          )}
        </View>
      )}
    </View>
  );
};

const TwoLineValue = ({
  pressHoverColor,
  item,
  item: { ValueIcon, bottomSide = 'left', align = bottomSide },
  C,
  textColor,
  valueColor,
  inverted,
  nameAlreadyTranslated,
  valueAlreadyTranslated,
  scale,
  font,
}) => {
  return (
    <View
      style={[
        {
          paddingRight: scale(15),
          alignItems: 'center',
          flexDirection: 'row',
          height: scale(51.5, false) - (isAndroid ? StyleSheet.hairlineWidth : 0),
        },
        isPhone && { justifyContent: 'space-between' },
      ]}
      key={item.name}
    >
      <View style={[{ alignItems: 'center', flexDirection: 'row', height: '100%', flex: 1 }, isPhone && { justifyContent: 'space-between' }]}>
        <View style={{ flex: 1, height: '100%', justifyContent: 'space-between', flexDirection: 'row', alignItems: 'center' }}>
          <View>
            <View>
              <Text
                numberOfLines={1}
                ellipsizeMode="tail"
                style={
                  inverted
                    ? [
                        {
                          color: C(pressHoverColor('Label/Light/Tertiary')),
                        },
                        font['Subhead 2'],
                      ]
                    : [
                        {
                          color: C(pressHoverColor(textColor)),
                        },
                        font.Body,
                      ]
                }
              >
                {item.name ? (nameAlreadyTranslated ? item.name : i18n.t(item.name)) : ''}
              </Text>
            </View>
            {bottomSide === 'left' && (item.value != null || item.ValueComponent) && !item.valueFunction ? (
              <View style={{ marginTop: scale(-1.5) }}>
                {item.ValueComponent && item.ValueComponent}
                {!item.ValueComponent && (
                  <Text
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    style={
                      inverted
                        ? [
                            {
                              color: C(pressHoverColor(textColor)),
                            },
                            font.Body,
                          ]
                        : [
                            {
                              color: C(pressHoverColor(valueColor)),
                            },
                            font['Caption 1'],
                          ]
                    }
                  >
                    {ValueIcon && <ValueIcon />}
                    {valueAlreadyTranslated ? (item.bottom ? item.bottom : item.value) : i18n.t(item.bottom ? item.bottom : item.value)}
                    {item.subValue ? (
                      <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>{` (${i18n.t(item.subValue)})`}</Text>
                    ) : (
                      ''
                    )}
                  </Text>
                )}
              </View>
            ) : null}
          </View>
          {item.secondName && (
            <View>
              <View>
                <Text
                  numberOfLines={1}
                  ellipsizeMode="tail"
                  style={
                    inverted
                      ? [
                          {
                            color: C(pressHoverColor('Label/Light/Tertiary')),
                          },
                          font['Subhead 2'],
                        ]
                      : [
                          {
                            color: C(pressHoverColor(textColor)),
                          },
                          font.Body,
                        ]
                  }
                >
                  {i18n.t(item.secondName || '')}
                </Text>
              </View>
              <View style={{ marginTop: scale(-1.5) }}>
                <Text
                  numberOfLines={1}
                  ellipsizeMode="tail"
                  style={[
                    inverted
                      ? [
                          {
                            color: C(pressHoverColor(textColor)),
                          },
                          font.Body,
                        ]
                      : [
                          {
                            color: C(pressHoverColor('Label / Light / Secondary')),
                          },
                          font['Caption 1'],
                        ],
                    {
                      opacity: item.secondValue ? 1 : 0,
                    },
                  ]}
                >
                  {item.secondValue ? i18n.t(item.secondValue) : '-'}
                </Text>
              </View>
            </View>
          )}
        </View>
        <View
          style={[
            { flexDirection: 'row', alignItems: 'center', height: '100%', justifyContent: 'space-between' },
            !isPhone && item?.shouldApplyFlex2 && { flex: 2 },
          ]}
        >
          <View>
            {Boolean(item.bottom) || item.valueFunction ? (
              <View>
                {item.valueFunction ? (
                  item.value()
                ) : (
                  <Text
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    style={[
                      {
                        color: C(pressHoverColor(valueColor)),
                        fontVariant: ['tabular-nums'],
                      },
                      font['Subhead 1'],
                    ]}
                  >
                    {item.value != null ? (valueAlreadyTranslated ? String(item.value) : i18n.t(String(item.value))) : ''}
                  </Text>
                )}
              </View>
            ) : null}
            {bottomSide === 'right' && Boolean(item.bottom) ? (
              <View>
                {
                  <Text
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    style={[
                      {
                        textAlign: align,
                        color: C(pressHoverColor(valueColor)),
                      },

                      font['Caption 1'],
                    ]}
                  >
                    {item.bottom ? i18n.t(String(item.bottom) || '') : ''}
                  </Text>
                }
              </View>
            ) : null}
          </View>
          {!item.noArrow && (
            <View style={{ paddingLeft: scale(10) }}>
              <Svg icon="chevronRightLight" color="Label/Light/Quinary" width={scale(7)} height={scale(12)} />
            </View>
          )}
        </View>
      </View>
    </View>
  );
};

export default (props) => {
  const {
    item,
    index,
    array,
    C,
    roundTop,
    roundBottom,
    topBorder = false,
    bottomBorder = false,
    topBorderSmall = false,
    bigSize = false,
    inverted = false,
    smallSize = false,
    bg = false,
    nameAlreadyTranslated = false,
    valueAlreadyTranslated = false,
    textColor = 'Label / Light / Primary',
    valueColor = 'Label / Light / Tertiary',
    bottomSide = 'left',
    align = bottomSide,
    takePhoneLayout = false,
    alwaysTwoLines = false,
    cursor,
    onlyLabel,
    scale = initialScale,
    font = initialFont,
  } = props;

  let styles = {};
  const lastIndex = index === array.length - 1;

  if (lastIndex && !bottomBorder) {
    styles = {
      ...styles,
      borderBottomWidth: 0,
    };
  }

  if (roundTop) {
    styles = {
      ...styles,
      borderTopLeftRadius: scale(SCREEN_PADDING_HORIZONTAL),
      borderTopRightRadius: scale(SCREEN_PADDING_HORIZONTAL),
    };
  }
  if (roundBottom) {
    styles = {
      ...styles,
      borderBottomLeftRadius: scale(SCREEN_PADDING_HORIZONTAL),
      borderBottomRightRadius: scale(SCREEN_PADDING_HORIZONTAL),
    };
  }

  // const fullText = `${nameAlreadyTranslated ? item.name : i18n.t(item.name)} ${valueAlreadyTranslated ? item.value : i18n.t(String(item.value))}`;

  // const lineWidth = width - scale(60);

  // const maxLength = isPhone ? lineWidth / scale(9) : 37;
  // const isLong = fullText.length > maxLength;
  const isLong = false;

  // const maxLengthSuper = isPhone ? lineWidth / scale(6) : 37;
  // const isSuperBig = isPhone && fullText.length > maxLengthSuper;
  const isSuperBig = false; // for now we are truncating the value with tail ellipsis mode, revert if needed

  // const shouldUseTwoLines = alwaysTwoLines || ((isLong || item.bottom || bigSize) && !smallSize && !isSuperBig && !item.defaultValue);
  const shouldUseTwoLines = alwaysTwoLines || ((isLong || item.bottom || bigSize) && !smallSize && !isSuperBig); // don't take into account defaultValue: boolean, because it might move value to second line, which we are trying to avoid now.

  return (
    <TouchableElement
      testID={item.testID}
      onPress={() => {
        if (item.onClick) {
          item.onClick();
        } else {
          if (isPhone && isSuperBig) {
            navigateTo('FieldInformation', { header: item.name, value: item.value });
          }
        }
      }}
      hitSlop={{}}
    >
      {({ pressHoverColor, boxStyle }) => (
        <View
          style={[
            (!item.noArrow || item.showHoverEffect) && boxStyle,
            {
              marginTop: isAndroid ? StyleSheet.hairlineWidth : 0,
              paddingLeft: scale(15),
              backgroundColor:
                !item.noArrow || item.showHoverEffect
                  ? C(pressHoverColor(bg || 'Background / Light / Field'))
                  : C(bg || 'Background / Light / Field'),
              zIndex: array.length - index,
            },
            topBorder &&
              index === 0 &&
              !topBorderSmall && {
                borderTopColor: C('Hairline / Light / No trans'),
                borderTopWidth: StyleSheet.hairlineWidth,
              },
            topBorder &&
              index === 0 &&
              !topBorderSmall && {
                borderTopColor: C('Hairline / Light / No trans'),
                borderTopWidth: StyleSheet.hairlineWidth,
              },
            lastIndex && bottomBorder && { borderBottomColor: C('Hairline / Light / No trans'), borderBottomWidth: StyleSheet.hairlineWidth },

            styles,
            isWeb && cursor && { cursor },
          ]}
        >
          <View
            style={[
              { width: '100%' },
              !lastIndex && { borderBottomColor: C('Hairline / Light / No trans'), borderBottomWidth: StyleSheet.hairlineWidth },
              topBorder &&
                index === 0 &&
                topBorderSmall && {
                  borderTopColor: C('Hairline / Light / No trans'),
                  borderTopWidth: StyleSheet.hairlineWidth,
                },
            ]}
          >
            {shouldUseTwoLines ? (
              <TwoLineValue
                {...{
                  pressHoverColor,
                  item,
                  C,
                  textColor,
                  valueColor,
                  inverted,
                  bottomSide,
                  align,
                  nameAlreadyTranslated,
                  valueAlreadyTranslated,
                  scale,
                  font,
                  onlyLabel,
                }}
              />
            ) : (
              <DefaultValue
                {...{
                  pressHoverColor,
                  item,
                  C,
                  textColor,
                  valueColor,
                  isSuperBig,
                  takePhoneLayout,
                  nameAlreadyTranslated,
                  valueAlreadyTranslated,
                  scale,
                  font,
                  onlyLabel,
                }}
              />
            )}
          </View>
        </View>
      )}
    </TouchableElement>
  );
};
